import { Input } from '@latitude/input';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react';

/**
 * A custom input component for commands.
 *
 * @component CommandInput
 * @param {string} className - The CSS class name for the component.
 * @param {string} inputSize - The size of the input.
 * @param {React.Ref} ref - The ref object for the component.
 * @param {object} props - Additional props for the component.
 * @returns {JSX.Element} - The rendered CommandInput component.
 */
export const CommandInput = ({
  className,
  inputSize,
  maxCharacters,
  characterLimitReachedLabel = 'Character limit reached',
  error,
  errorMessageId,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof CommandPrimitive.Input> &
  ComponentPropsWithoutRef<typeof Input> & {
    maxCharacters?: number;
    characterLimitReachedLabel?: string;
    errorMessageId?: string;
    ref?: Ref<ComponentRef<typeof CommandPrimitive.Input>>;
  }) => {
  const search = useCommandState((state) => state.search);
  const showCounter = maxCharacters !== undefined && !error;
  const limitReached = showCounter && search.length >= maxCharacters;

  return (
    <div
      className={cn(
        'mb-spacing-comp-action-list-medium-vertical sticky top-0',
        className,
      )}
    >
      <CommandPrimitive.Input ref={ref} {...props} asChild>
        <Input
          data-testid="command-input"
          inputSize={inputSize}
          error={error}
          search
          classNames={{
            wrapper:
              '[&:has(input:focus):has(input[aria-invalid="true"])]:before:border-color-comp-text-field-border-danger',
            trailingElement:
              'flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4',
          }}
          rawProps={{
            ...(maxCharacters !== undefined && { maxLength: maxCharacters }),
            ...(error && errorMessageId
              ? { 'aria-describedby': errorMessageId }
              : {}),
          }}
        />
      </CommandPrimitive.Input>
      {showCounter && (
        <div
          className="pt-spacing-sys-spacing-4 flex justify-between"
          data-testid="command-input-counter"
        >
          <Text variant="captionRegular" color="muted">
            {limitReached ? characterLimitReachedLabel : ''}
          </Text>
          <Text variant="captionRegular" color="muted">
            {search.length}/{maxCharacters}
          </Text>
        </div>
      )}
    </div>
  );
};

CommandInput.displayName = CommandPrimitive.Input.displayName;
